/**
 * Foundations: the seven small models behind `/foundations`, and the record
 * of which steps this browser has passed.
 *
 * docs/foundations.md is the contract. The short version: every step is a
 * task the learner does and the machine checks — never a question with four
 * answers — and every check is a pure function of what the learner set, so
 * the page, the tests and (one day) a server can all reach the same verdict
 * from the same numbers.
 *
 * Each model captures just enough physics for the one decision its step asks
 * for, and says what it leaves out (the `leavesOut` line in the strings).
 * They are simplified on purpose. A shelf here is a uniform plank under a
 * uniform load; a real one has knots, screws and a bracket that bends.
 *
 * The numbers follow the Movie Science Shorts the steps open with
 * (videos/movie-science/<slug>/script.md, "Facts to keep straight"), so a
 * learner who watched the Short meets the same figures here.
 */

/** The steps, in order. Also the closed list the analytics event may carry. */
export const STEP_IDS = ['estimate', 'loads', 'power', 'margin', 'weakest', 'tradeoff', 'check-ai'] as const;
export type StepId = (typeof STEP_IDS)[number];

/**
 * The published Short each step opens with, by YouTube id.
 *
 * Only Shorts that are on YouTube belong here; a step without one simply has
 * no "watch" link. They open on YouTube in a new tab: the site's policy
 * refuses frames (web/public/_headers, `frame-src 'none'`), and a link keeps
 * YouTube's player and its cookies off this page entirely.
 */
export const STEP_SHORTS: Partial<Record<StepId, string>> = {
  estimate: 'Z1sjWEmsJAY', // The Matrix: humans as batteries
  power: 'iVS6zVWq1Kw', // Iron Man: three gigawatts through a wire
  tradeoff: 'qFblTcDoF4c', // Gravity: a jetpack to the space station
};

export function shortUrl(id: string): string {
  return `https://youtube.com/shorts/${id}`;
}

const G = 9.81;

/* ── 1. Estimation ─────────────────────────────────────────────────────── */

/** A resting adult gives off about 100 W (2,000 kcal a day ≈ 97 W). */
export const BODY_WATTS = 100;

/** The guess slider, as watts: one stop per factor of about three. */
export const GUESS_STOPS = [1, 3, 10, 30, 100, 300, 1000, 3000, 10000] as const;

export interface EstimateResult {
  ok: boolean;
  /** How many times off the guess is, always ≥ 1. */
  factor: number;
  direction: 'low' | 'high' | 'close';
}

/** Right to within about three times either way (under four): estimation is about the order, not the digit. */
export function checkEstimate(watts: number): EstimateResult {
  const ratio = watts / BODY_WATTS;
  const factor = ratio >= 1 ? ratio : 1 / ratio;
  const ok = factor < 4;
  return { ok, factor, direction: ok ? 'close' : ratio < 1 ? 'low' : 'high' };
}

/* ── 2 and 7. A shelf under books ──────────────────────────────────────── */

/** Pine, along the grain. */
const PINE_E = 9e9; // Pa
/** Plank depth, front to back. */
const PLANK_DEPTH = 0.25; // m
/** Books: about 25 kg on every metre of shelf. */
export const BOOKS_KG_PER_M = 25;

/** Planks as a timber yard sells them, in millimetres. */
export const PLANK_STOPS = [12, 15, 18, 22, 25, 30] as const;

export interface ShelfInput {
  /** Bracket to bracket, end to end, metres. */
  length: number;
  /** Plank thickness, millimetres. */
  thickness: number;
  /** Two brackets (one span) or three (two equal spans). */
  brackets: 2 | 3;
}

export interface ShelfResult {
  ok: boolean;
  sagMm: number;
  /** The usual limit for a shelf: span / 200. */
  limitMm: number;
}

/**
 * Deflection of a uniform plank under a uniform load.
 *
 * Two brackets: a simply supported span, δ = 5wL⁴ / 384EI. Three: two equal
 * continuous spans, whose largest sag is wl⁴ / 185EI with l the half length.
 * The limit is set on the longest span, which is what a reader's eye sees.
 */
export function shelf({ length, thickness, brackets }: ShelfInput): ShelfResult {
  const w = BOOKS_KG_PER_M * G; // N/m
  const t = thickness / 1000;
  const I = (PLANK_DEPTH * t ** 3) / 12;
  const span = brackets === 3 ? length / 2 : length;
  const sag = brackets === 3 ? (w * span ** 4) / (185 * PINE_E * I) : (5 * w * span ** 4) / (384 * PINE_E * I);
  const sagMm = sag * 1000;
  const limitMm = (span * 1000) / 200;
  return { ok: sagMm <= limitMm, sagMm, limitMm };
}

/** Step 2: a one-metre alcove, two brackets. */
export const LOADS_LENGTH = 1.0;

/** Step 7: Iris's plan for a longer alcove, as it arrives. */
export const IRIS_PLAN: ShelfInput = { length: 1.5, thickness: 18, brackets: 2 };

/* ── 3. Power through a wire ───────────────────────────────────────────── */

/** Tony Stark's reactor: three gigajoules a second. */
export const REACTOR_WATTS = 3e9;
/** What one 4 cm line conductor carries (the Pacific DC Intertie: 3.1 GW at ±500 kV). */
export const CABLE_AMPS = 3100;
/** What this line's towers and insulators can hold apart. */
export const TOWER_VOLTS = 1_000_000;

/** A car battery, a wall socket, then the voltages grids actually use. */
export const VOLT_STOPS = [12, 230, 11_000, 132_000, 250_000, 400_000, 500_000, 765_000, 1_000_000, 1_500_000] as const;

export interface PowerResult {
  ok: boolean;
  amps: number;
  ampsPerCable: number;
  /** Over the rating, as a multiple; < 1 is within it. */
  load: number;
  /** The voltage is more than the towers can insulate. */
  arcs: boolean;
}

/** Power is volts times amps; each cable may carry its rating; the towers cap the voltage. */
export function powerLine(volts: number, cables: number): PowerResult {
  const amps = REACTOR_WATTS / volts;
  const ampsPerCable = amps / cables;
  const load = ampsPerCable / CABLE_AMPS;
  const arcs = volts > TOWER_VOLTS;
  return { ok: load <= 1 && !arcs, amps, ampsPerCable, load, arcs };
}

/* ── 4. A safety margin ────────────────────────────────────────────────── */

/** The load the hoist lifts. */
export const HOIST_KG = 500;
/** The brief's required margin: the rope must be five times stronger than the load. */
export const REQUIRED_FACTOR = 5;
/** Steel wire rope, by diameter in millimetres. */
export const ROPE_STOPS = [4, 5, 6, 7, 8, 10, 12] as const;

/** A rule of thumb for steel wire rope: breaking force ≈ 0.6 × d² kN, d in mm. */
export function ropeBreakingKn(diameterMm: number): number {
  return 0.6 * diameterMm ** 2;
}

export interface MarginResult {
  ok: boolean;
  loadKn: number;
  breakingKn: number;
  factor: number;
}

export function margin(diameterMm: number): MarginResult {
  const loadKn = (HOIST_KG * G) / 1000;
  const breakingKn = ropeBreakingKn(diameterMm);
  const factor = breakingKn / loadKn;
  return { ok: factor >= REQUIRED_FACTOR, loadKn, breakingKn, factor };
}

/* ── 5. The weakest link ───────────────────────────────────────────────── */

export const LINK_IDS = ['anchor', 'rope', 'shackle', 'hook'] as const;
export type LinkId = (typeof LINK_IDS)[number];

/** Each part's strength as fitted, in kN. */
export const LINK_BASE: Record<LinkId, number> = { anchor: 18, rope: 45, shackle: 25, hook: 40 };
/** What one upgrade adds to any part. */
export const LINK_STEP = 8;
/** At most this many upgrades on one part. */
export const LINK_MAX = 2;
/** Upgrades the budget pays for, in all. */
export const LINK_BUDGET = 3;
/** The load the chain must hold. */
export const LINK_TARGET = 30;

export type Upgrades = Record<LinkId, number>;

export const NO_UPGRADES: Upgrades = { anchor: 0, rope: 0, shackle: 0, hook: 0 };

export interface ChainResult {
  ok: boolean;
  /** The chain holds what its weakest part holds. */
  holdsKn: number;
  /** The part that breaks first under a rising load. */
  breaks: LinkId;
  spent: number;
  strengths: Record<LinkId, number>;
}

export function chain(upgrades: Upgrades): ChainResult {
  const strengths = {} as Record<LinkId, number>;
  let breaks: LinkId = LINK_IDS[0];
  let spent = 0;
  for (const id of LINK_IDS) {
    const n = Math.max(0, Math.min(LINK_MAX, Math.round(upgrades[id] ?? 0)));
    spent += n;
    strengths[id] = LINK_BASE[id] + n * LINK_STEP;
    if (strengths[id] < strengths[breaks]) breaks = id;
  }
  const holdsKn = strengths[breaks];
  return { ok: holdsKn >= LINK_TARGET && spent <= LINK_BUDGET, holdsKn, breaks, spent, strengths };
}

/* ── 6. A trade-off ────────────────────────────────────────────────────── */

/** NASA's Manned Maneuvering Unit could change its speed by about 24 m/s in all. */
export const JETPACK_DV = 24;
/** Astronaut and jetpack, kg. */
const ASTRONAUT_KG = 300;
/** The repair kit the airlock crew is waiting for, kg. */
export const KIT_KG = 60;
/** From here to the airlock. */
export const AIRLOCK_M = 120;
/** The crew can hold the airlock open this long. */
export const DEADLINE_S = 20;
/** Fuel kept back for a mistake, as speed change. */
export const RESERVE_DV = 4;

export interface JetpackResult {
  ok: boolean;
  /** Speed change the pack can give with this load. */
  budget: number;
  /** Speeding up and then stopping both cost the cruise speed. */
  used: number;
  reserve: number;
  seconds: number;
  /** Which promise this plan breaks first, if any. */
  broken: 'kit' | 'fuel' | 'reserve' | 'late' | null;
}

/**
 * Same push, more mass, less speed change: Δv scales with 1/mass for a fixed
 * impulse, which is all this needs. Burns are treated as instant.
 */
export function jetpack(speed: number, kit: boolean): JetpackResult {
  const budget = kit ? (JETPACK_DV * ASTRONAUT_KG) / (ASTRONAUT_KG + KIT_KG) : JETPACK_DV;
  const used = 2 * speed;
  const reserve = budget - used;
  const seconds = AIRLOCK_M / speed;
  const broken = !kit ? 'kit' : reserve < 0 ? 'fuel' : reserve < RESERVE_DV ? 'reserve' : seconds > DEADLINE_S ? 'late' : null;
  return { ok: broken === null, budget, used, reserve, seconds, broken };
}

/* ── The vote ─────────────────────────────────────────────────────────── */

const VOTE_KEY = 'engineeros.foundations.vote';

/** The disciplines a finisher can ask for. Software is open; the rest are counted. */
export const DISCIPLINES = ['software', 'power', 'civil'] as const;
export type Discipline = (typeof DISCIPLINES)[number];

export function loadVotes(): Set<Discipline> {
  try {
    const raw = JSON.parse(localStorage.getItem(VOTE_KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.filter((d): d is Discipline => (DISCIPLINES as readonly string[]).includes(d as string)));
  } catch {
    return new Set();
  }
}

export function saveVotes(votes: ReadonlySet<Discipline>): void {
  try {
    localStorage.setItem(VOTE_KEY, JSON.stringify([...votes]));
  } catch {
    /* private mode, or storage disabled */
  }
}
